import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import casinoService from '../../services/casino.service'
import { MAIN_CASINO_PATH } from '../../constants/mainCasinoRoute'
import { LOTUS_TOP_PROVIDERS } from '../../constants/lotusCasinoTopProviders'
import {
  getLotusProviderIconCandidates,
} from '../../utils/lotusProviderIcons'
import '../../apollo-icons.css'
import './LotusCasinoProvidersPage.css'

export type LotusCasinoProvidersPageProps = {
  preLoginMode?: boolean
  onBack?: () => void
  onProviderSelect?: (categoryId: string) => void
}

type ProviderRow = {
  id: string
  count: number | null
}

function ProviderIcon({ name, localSlug }: { name: string; localSlug?: string }) {
  const candidates = useMemo(
    () => getLotusProviderIconCandidates(name, localSlug),
    [name, localSlug],
  )
  const [index, setIndex] = useState(0)

  useEffect(() => {
    setIndex(0)
  }, [name, localSlug])

  const src = candidates[index]
  if (!src || index >= candidates.length) {
    return <span className="lotus-casino-providers__provider-icon is-empty" aria-hidden />
  }

  return (
    <img
      className="lotus-casino-providers__provider-icon"
      src={src}
      alt=""
      width={40}
      height={40}
      loading="lazy"
      onError={() => setIndex((i) => i + 1)}
    />
  )
}

const LotusCasinoProvidersPage: React.FC<LotusCasinoProvidersPageProps> = ({
  preLoginMode = false,
  onBack,
  onProviderSelect,
}) => {
  const navigate = useNavigate()
  const [providers, setProviders] = useState<ProviderRow[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')

  const fetchList = useCallback(
    (provider: string, type = 'slots') =>
      preLoginMode
        ? casinoService.getPublicIntCasinoList(provider, type)
        : casinoService.getIntCasinoList(provider, type),
    [preLoginMode],
  )

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      try {
        const res = await fetchList('100hp', 'slots')
        const list = res.data?.providers
        if (!Array.isArray(list)) {
          if (!cancelled) {
            setProviders([])
            setLoading(false)
          }
          return
        }

        const rows: ProviderRow[] = list.map((p: { _id: string }) => ({ id: p._id, count: null }))
        if (!cancelled) {
          setProviders(rows)
          setLoading(false)
        }

        await Promise.all(
          rows.map(async (row, index) => {
            try {
              const gameRes = await fetchList(row.id, 'slots')
              const games = gameRes.data?.result
              const count = Array.isArray(games) ? games.length : 0
              if (!cancelled) {
                setProviders((prev) => {
                  const next = [...prev]
                  if (next[index]) next[index] = { ...next[index], count }
                  return next
                })
              }
            } catch {
              /* keep null count */
            }
          }),
        )
      } catch {
        if (!cancelled) {
          setProviders([])
          setLoading(false)
        }
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [fetchList])

  const handleBack = () => {
    if (onBack) {
      onBack()
      return
    }
    navigate(MAIN_CASINO_PATH)
  }

  const handleProvider = (categoryId: string) => {
    if (preLoginMode && onProviderSelect) {
      onProviderSelect(categoryId)
      return
    }
    navigate(`/casino-int/slots?category=${encodeURIComponent(categoryId)}`)
  }

  const normalizedQuery = query.trim().toLowerCase()

  const filteredAll = useMemo(() => {
    if (!normalizedQuery) return providers
    return providers.filter((p) => p.id.toLowerCase().includes(normalizedQuery))
  }, [providers, normalizedQuery])

  const topRows = useMemo(() => {
    return LOTUS_TOP_PROVIDERS.map((top) => {
      const match = providers.find(
        (p) =>
          p.id.toLowerCase() === top.categoryId.toLowerCase() ||
          p.id.toLowerCase() === top.label.toLowerCase() ||
          p.id.toLowerCase() === top.id.toLowerCase(),
      )
      return {
        id: top.categoryId,
        label: top.label,
        count: match?.count ?? null,
        iconName: top.label,
        localSlug: top.iconSlug,
      }
    }).filter((row) => {
      if (!normalizedQuery) return true
      return row.label.toLowerCase().includes(normalizedQuery)
    })
  }, [providers, normalizedQuery])

  const renderCard = (
    key: string,
    label: string,
    categoryId: string,
    count: number | null,
    iconName: string,
    localSlug?: string,
  ) => (
    <div
      key={key}
      className="lotus-casino-providers__group-card"
      onClick={() => handleProvider(categoryId)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleProvider(categoryId)}
    >
      <div className="lotus-casino-providers__group-left">
        <ProviderIcon name={iconName} localSlug={localSlug} />
        <span className="lotus-casino-providers__provider-name">{label}</span>
      </div>
      <div className="lotus-casino-providers__group-right">
        <span className="lotus-casino-providers__table-count">{count ?? '—'}</span>
      </div>
    </div>
  )

  return (
    <div className="lotus-casino-providers">
      <button type="button" className="lotus-casino-providers__back" onClick={handleBack}>
        <i className="icon icon-angle-left" aria-hidden />
        <span>Back</span>
      </button>

      {/* Title + search (must stay above Top Providers — no conflicting global class names) */}
      <div className="lotus-casino-providers__header">
        <div className="lotus-casino-providers__title-row">
          <h1 className="lotus-casino-providers__title">Providers</h1>
          <button
            type="button"
            className="lotus-casino-providers__close"
            onClick={handleBack}
            aria-label="Close providers"
          >
            <i className="icon icon-cross" aria-hidden />
          </button>
        </div>
        <div className="lotus-casino-providers__input-wrapper">
          <i className="icon icon-search-lite" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a Provider name"
            autoComplete="off"
          />
        </div>
      </div>

      <div className="lotus-casino-providers__results">
        {topRows.length > 0 && (
          <section className="lotus-casino-providers__section">
            <div className="lotus-casino-providers__grouped-title">Top Providers</div>
            <div className="lotus-casino-providers__grouped-grid">
              {topRows.map((row) =>
                renderCard(row.id, row.label, row.id, row.count, row.iconName, row.localSlug),
              )}
            </div>
          </section>
        )}

        <section className="lotus-casino-providers__section lotus-casino-providers__section--all">
          <div className="lotus-casino-providers__grouped-title">All Providers</div>
          <div className="lotus-casino-providers__grouped-grid">
            {loading && providers.length === 0 &&
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="lotus-casino-providers__skeleton" />
              ))}
            {!loading && filteredAll.length === 0 && (
              <div className="lotus-casino-providers__empty">No providers found.</div>
            )}
            {filteredAll.map((prov) =>
              renderCard(prov.id, prov.id, prov.id, prov.count, prov.id),
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

export default LotusCasinoProvidersPage
